import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import CurrencyLogo from 'components/CurrencyLogo'
import Modal from 'components/Modal'
import { useApiTokenValue } from 'hooks/useTokenPrice'
import { useCallback, useMemo } from 'react'
import { Send, User } from 'react-feather'
import styled from 'styled-components'
import { ThemedText } from 'theme'
import { isAddress } from 'utils'
import { formatDollarAmount } from 'utils/formatDollarAmt'

import TransactionError from '../TransactionError'

const ClaimStepContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 8px 4px;
  max-width: 480px;
`

const HeaderText = styled(ThemedText.DeprecatedSubHeader)`
  margin: 0 0 4px 0;
  font-size: 20px;
  font-weight: 500;
`

const RewardSection = styled.div`
  margin-bottom: 8px;
`

const RewardAmount = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.cardTertiary};
  border-radius: 12px;
`

const SectionLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
  margin-bottom: 8px;
  margin-top: 16px;
`

const WalletOptions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const WalletOption = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  border-radius: 12px;
  cursor: pointer;
  border: 1px solid ${({ selected, theme }) => (selected ? theme.accentActive : 'transparent')};
  background: ${({ selected, theme }) => (selected ? `${theme.cardSecondary}` : theme.cardTertiary)};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme, selected }) => (!selected ? theme.cardTertiary : theme.cardSecondary)};
  }
`

const IconWrapper = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${({ theme, selected }) => (selected ? theme.accentActive : theme.backgroundInteractive)};
  color: ${({ theme, selected }) => (selected ? 'white' : theme.textSecondary)};
`

const WalletDetails = styled.div`
  flex: 1;
`

const WalletLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.textPrimary};
`

const WalletAddress = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.textSecondary};
  margin-top: 2px;
`

const AddressInput = styled.input`
  width: 100%;
  margin-top: 8px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.backgroundInteractive};
  background: ${({ theme }) => theme.backgroundSurface};
  color: ${({ theme }) => theme.textPrimary};
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.accentActive};
  }

  &::placeholder {
    color: ${({ theme }) => theme.textTertiary};
  }
`

const ConfirmButton = styled.button<{ disabled?: boolean }>`
  width: 100%;
  padding: 14px;
  margin-top: 12px;
  border-radius: 12px;
  border: none;
  background: ${({ disabled, theme }) => (disabled ? theme.backgroundInteractive : theme.accentAction)};
  color: white;
  font-size: 16px;
  font-weight: 500;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ disabled }) => (disabled ? 0.6 : 1)};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.hoverState};
  }
`

type RewardVaultClaimModalProps = {
  isOpen: boolean
  onDismiss: () => void
  onConfirm: () => void
  earnedAmount: CurrencyAmount<Token> | undefined
  account: string | undefined
  claimDestination: 'connected' | 'other'
  setClaimDestination: (destination: 'connected' | 'other') => void
  otherWalletAddress: string
  setOtherWalletAddress: (address: string) => void
  error?: string
}

export default function RewardVaultClaimModal({
  isOpen,
  onDismiss,
  onConfirm,
  earnedAmount,
  account,
  claimDestination,
  setClaimDestination,
  otherWalletAddress,
  setOtherWalletAddress,
  error,
}: RewardVaultClaimModalProps) {
  const { value: fiatValue } = useApiTokenValue(earnedAmount)
  const rewardSymbol = earnedAmount?.currency.symbol
  const claimTitle = rewardSymbol ? `Claim ${rewardSymbol} Rewards` : 'Claim Rewards'

  const truncateAddress = useCallback((address: string | null | undefined) => {
    if (!address) return 'Not connected'
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`
  }, [])

  const isButtonDisabled = useMemo(() => {
    if (!earnedAmount || earnedAmount.equalTo('0')) return true
    if (claimDestination === 'other' && (!otherWalletAddress || !isAddress(otherWalletAddress))) {
      return true
    }
    return false
  }, [earnedAmount, claimDestination, otherWalletAddress])

  return (
    <Modal isOpen={isOpen} onDismiss={onDismiss} maxHeight={90} disableDismiss={false}>
      <ClaimStepContainer>
        <HeaderText>{claimTitle}</HeaderText>

        <RewardSection>
          <SectionLabel>Available Rewards to claim</SectionLabel>
          <RewardAmount>
            <CurrencyLogo currency={earnedAmount?.currency} size="24px" />
            <ThemedText.DeprecatedBody marginLeft="10px" fontWeight="600" fontSize="16px">
              {earnedAmount?.toSignificant(4)} {earnedAmount?.currency.symbol}
            </ThemedText.DeprecatedBody>
            <ThemedText.DeprecatedBody marginLeft="auto" color="green" fontSize="15px">
              {formatDollarAmount(fiatValue)}
            </ThemedText.DeprecatedBody>
          </RewardAmount>
        </RewardSection>

        <SectionLabel>Claim to</SectionLabel>
        <WalletOptions>
          <WalletOption selected={claimDestination === 'connected'} onClick={() => setClaimDestination('connected')}>
            <IconWrapper selected={claimDestination === 'connected'}>
              <User size={18} />
            </IconWrapper>
            <WalletDetails>
              <WalletLabel>Connected Wallet</WalletLabel>
              <WalletAddress>{truncateAddress(account)}</WalletAddress>
            </WalletDetails>
          </WalletOption>

          <WalletOption selected={claimDestination === 'other'} onClick={() => setClaimDestination('other')}>
            <IconWrapper selected={claimDestination === 'other'}>
              <Send size={18} />
            </IconWrapper>
            <WalletDetails>
              <WalletLabel>Another Wallet</WalletLabel>
              {claimDestination === 'other' && (
                <>
                  <AddressInput
                    placeholder="Enter wallet address"
                    value={otherWalletAddress}
                    onChange={(e) => setOtherWalletAddress(e.target.value)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  {otherWalletAddress && !isAddress(otherWalletAddress) && (
                    <WalletAddress style={{ color: 'red', marginTop: '4px' }}>
                      Please enter a valid address
                    </WalletAddress>
                  )}
                </>
              )}
            </WalletDetails>
          </WalletOption>
        </WalletOptions>

        <ConfirmButton onClick={onConfirm} disabled={isButtonDisabled}>
          Confirm
        </ConfirmButton>

        {error && <TransactionError error={error} />}
      </ClaimStepContainer>
    </Modal>
  )
}
